import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useNotifications, type Notification } from '@/hooks/useNotifications';

const typeConfig: Record<string, { icon: string; bg: string; color: string; label: string }> = {
  match_request: { icon: 'ri-mail-send-line', bg: 'bg-[#E3F2FD]', color: 'text-[#1976D2]', label: '매칭 요청' },
  match_accepted: { icon: 'ri-check-double-line', bg: 'bg-[#E8F5E9]', color: 'text-[#2E7D6B]', label: '매칭 수락' },
  match_rejected: { icon: 'ri-close-circle-line', bg: 'bg-[#FFEBEE]', color: 'text-[#D32F2F]', label: '매칭 거절' },
  match_completed: { icon: 'ri-calendar-check-line', bg: 'bg-[#FFF3E0]', color: 'text-[#F57C00]', label: '간병 완료' },
  review_received: { icon: 'ri-star-line', bg: 'bg-[#FFF8E1]', color: 'text-[#F9A825]', label: '리뷰 도착' },
  chat_message: { icon: 'ri-chat-3-line', bg: 'bg-[#E8F5E9]', color: 'text-[#2E7D6B]', label: '새 메시지' },
  system: { icon: 'ri-megaphone-line', bg: 'bg-[#F3E5F5]', color: 'text-[#7B1FA2]', label: '공지' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '방금 전';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return new Date(dateStr).toLocaleDateString('ko-KR');
}

export default function AppNotificationsPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function handleClick(notif: Notification) {
    if (!notif.is_read) await markAsRead(notif.id);
    setExpandedId(expandedId === notif.id ? null : notif.id);

    // 알림 타입에 따라 관련 페이지로 이동
    if (notif.related_id) {
      if (notif.type === 'chat_message') {
        navigate(`/app/chat?match_id=${notif.related_id}`);
      } else if (['match_request', 'match_accepted', 'match_rejected', 'match_completed'].includes(notif.type)) {
        navigate('/app/matching');
      } else if (notif.type === 'review_received') {
        navigate('/app/my-reviews');
      }
    }
  }

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen pb-24 bg-[#F8F9FA]">
        {/* 헤더 */}
        <div className="px-5 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <i className="ri-arrow-left-line text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">알림</h1>
              {unreadCount > 0 && (
                <p className="text-xs text-[#2E7D6B] font-medium">읽지 않은 알림 {unreadCount}개</p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs font-medium text-[#2E7D6B] px-3 py-1.5 rounded-lg bg-[#E8F5E9] cursor-pointer hover:bg-[#C8E6C9] transition-colors whitespace-nowrap"
            >
              <i className="ri-check-double-line mr-1" />
              모두 읽음
            </button>
          )}
        </div>

        {/* 알림 목록 */}
        <div className="px-5">
          {notifications.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-off-line text-2xl text-gray-300" />
              </div>
              <p className="text-sm text-gray-400 font-medium">알림이 없습니다</p>
              <p className="text-xs text-gray-300 mt-1">매칭 요청이나 상태 변경 시 여기에 표시됩니다</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notif) => {
                const cfg = typeConfig[notif.type] || typeConfig.system;
                const isExpanded = expandedId === notif.id;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left rounded-2xl border transition-all duration-200 cursor-pointer ${
                      notif.is_read
                        ? 'bg-white border-gray-100'
                        : 'bg-[#f0f9f6] border-[#b8e6d8]'
                    }`}
                  >
                    <div className="p-4 flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                        <i className={`${cfg.icon} ${cfg.color} text-lg`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                            {cfg.label}
                          </span>
                          {!notif.is_read && (
                            <span className="w-2 h-2 rounded-full bg-[#2E7D6B] shrink-0" />
                          )}
                        </div>
                        <p className="text-sm font-bold text-gray-900">{notif.title}</p>
                        {notif.body && (
                          <p
                            className={`text-xs text-gray-500 mt-0.5 transition-all duration-200 ${
                              isExpanded ? '' : 'line-clamp-2'
                            }`}
                          >
                            {notif.body}
                          </p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1.5">{timeAgo(notif.created_at)}</p>
                      </div>
                      <i
                        className={`ri-arrow-right-s-line text-gray-300 transition-transform duration-200 ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}